import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const AuditTimelineEntry = ({ log }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getActionColor = (actionType) => {
    switch (actionType) {
      case 'CREATE':
        return 'text-success bg-success/10';
      case 'UPDATE':
        return 'text-warning bg-warning/10';
      case 'DELETE':
        return 'text-error bg-error/10';
      case 'VIEW':
        return 'text-primary bg-primary/10';
      case 'EXPORT':
        return 'text-accent bg-accent/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'CREATE':
        return 'Plus';
      case 'UPDATE':
        return 'Edit';
      case 'DELETE':
        return 'Trash2';
      case 'VIEW':
        return 'Eye';
      case 'EXPORT':
        return 'Download';
      default:
        return 'Activity';
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const renderChanges = () => {
    if (!log?.changes) return null;

    const { old: oldData, new: newData } = log?.changes;

    if (log?.actionType === 'CREATE' && newData) {
      return (
        <div className="space-y-2">
          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Created Data</h5>
          <div className="bg-success/5 border border-success/20 rounded-lg p-3">
            <pre className="text-xs text-foreground overflow-x-auto">
              {JSON.stringify(newData, null, 2)}
            </pre>
          </div>
        </div>
      );
    }

    if (log?.actionType === 'DELETE' && oldData) {
      return (
        <div className="space-y-2">
          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Deleted Data</h5>
          <div className="bg-error/5 border border-error/20 rounded-lg p-3">
            <pre className="text-xs text-foreground overflow-x-auto">
              {JSON.stringify(oldData, null, 2)}
            </pre>
          </div>
        </div>
      );
    }

    if (log?.actionType === 'UPDATE' && oldData && newData) {
      return (
        <div className="space-y-3">
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Before</h5>
            <div className="bg-muted/50 border border-border rounded-lg p-3">
              <pre className="text-xs text-foreground overflow-x-auto">
                {JSON.stringify(oldData, null, 2)}
              </pre>
            </div>
          </div>
          <div className="space-y-2">
            <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">After</h5>
            <div className="bg-warning/5 border border-warning/20 rounded-lg p-3">
              <pre className="text-xs text-foreground overflow-x-auto">
                {JSON.stringify(newData, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-therapeutic transition-smooth hover:shadow-therapeutic-md">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded-xl"
        aria-expanded={isExpanded}
      >
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${getActionColor(log?.actionType)}`}>
            <Icon name={getActionIcon(log?.actionType)} size={18} strokeWidth={2} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="text-sm font-medium text-foreground">
                  {log?.actionType} - {log?.resourceType?.replace('_', ' ')}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  by {log?.user?.email || 'Unknown User'}
                </p>
              </div>
              <Icon 
                name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-muted-foreground flex-shrink-0"
                strokeWidth={2}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={12} strokeWidth={2} />
                {formatTimestamp(log?.createdAt)}
              </span>
              {log?.resourceId && (
                <span className="flex items-center gap-1">
                  <Icon name="Hash" size={12} strokeWidth={2} />
                  {log?.resourceId?.substring(0, 8)}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="h-px bg-border" />

          {/* User Details */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <Icon name="User" size={16} strokeWidth={2} />
              User Information
            </h4>
            <div className="bg-muted/50 rounded-lg p-3 space-y-1">
              <p className="text-sm text-foreground">
                <span className="font-medium">Email:</span> {log?.user?.email || 'N/A'}
              </p>
              {log?.user?.firstName && (
                <p className="text-sm text-foreground">
                  <span className="font-medium">Name:</span> {log?.user?.firstName} {log?.user?.lastName || ''}
                </p>
              )}
              {log?.user?.userType && (
                <p className="text-sm text-foreground">
                  <span className="font-medium">User Type:</span> {log?.user?.userType}
                </p>
              )}
            </div>
          </div>

          {/* Changes */}
          {renderChanges()}

          {/* Metadata */}
          {log?.metadata && Object.keys(log?.metadata)?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Info" size={16} strokeWidth={2} />
                Additional Information
              </h4>
              <div className="bg-muted/50 rounded-lg p-3">
                <pre className="text-xs text-foreground overflow-x-auto">
                  {JSON.stringify(log?.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Technical Details */}
          {(log?.ipAddress || log?.userAgent) && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Icon name="Server" size={16} strokeWidth={2} />
                Technical Details
              </h4>
              <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                {log?.ipAddress && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">IP Address:</span> {log?.ipAddress}
                  </p>
                )}
                {log?.userAgent && (
                  <p className="text-xs text-muted-foreground break-all">
                    <span className="font-medium">User Agent:</span> {log?.userAgent}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AuditTimelineEntry;